import { useCallback, useEffect, useRef } from 'react';

type Props = {
  itemCount: number;
  hasMore?: boolean;
  isLoading?: boolean;
  loadMore?: () => Promise<object> | void;
};

export const useInfiniteListFill = ({ itemCount, hasMore, isLoading, loadMore }: Props) => {
  const scrollerRef = useRef<HTMLElement | null>(null);
  const listHeightRef = useRef(0);

  const pending = useRef(false);
  const pendingAtCount = useRef(-1);
  const pendingRenders = useRef(0);
  const sawLoading = useRef(false);
  const fillStalled = useRef(false);

  const request = useCallback(() => {
    if (!hasMore || isLoading || !loadMore || pending.current) return;
    pending.current = true;
    pendingRenders.current = 0;
    pendingAtCount.current = itemCount;
    void loadMore();
  }, [hasMore, isLoading, itemCount, loadMore]);

  const fillViewport = useCallback(() => {
    if (fillStalled.current) return;
    const scroller = scrollerRef.current;
    if (!scroller || scroller.clientHeight === 0) return;
    if (listHeightRef.current <= scroller.clientHeight) request();
  }, [request]);

  const handleScrollerRef = useCallback((element: HTMLElement | Window | null) => {
    scrollerRef.current = element instanceof HTMLElement ? element : null;
  }, []);

  const handleTotalListHeightChanged = useCallback(
    (height: number) => {
      listHeightRef.current = height;
      fillViewport();
    },
    [fillViewport],
  );

  const handleEndReached = useCallback(() => {
    fillStalled.current = false;
    request();
  }, [request]);

  useEffect(() => {
    if (isLoading) {
      sawLoading.current = true;
      return;
    }

    if (pending.current) {
      if (itemCount !== pendingAtCount.current) {
        pending.current = false;
      } else if (sawLoading.current) {
        pending.current = false;
        fillStalled.current = true;
      } else {
        pendingRenders.current += 1;
        if (pendingRenders.current > 1) {
          pending.current = false;
          fillStalled.current = true;
        }
      }
    } else if (sawLoading.current) {
      fillStalled.current = false;
    }

    sawLoading.current = false;
    fillViewport();
  });

  return { handleScrollerRef, handleTotalListHeightChanged, handleEndReached };
};
